import { useEffect, useRef, useState } from "react";
import { Star, ExternalLink, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { GOOGLE_REVIEWS_URL } from "@/data/site";
import { Eyebrow } from "./Blocks";

export const Stars = ({ n, className = "h-4 w-4" }) => (
  <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${className} ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
  </span>
);

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="#4285F4" d="M22.6 12.3c0-.8-.1-1.5-.2-2.3H12v4.3h5.9a5 5 0 0 1-2.2 3.3v2.8h3.6c2.1-1.9 3.3-4.8 3.3-8.1z" /><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.8c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.6H2.1v2.9A11 11 0 0 0 12 23z" /><path fill="#FBBC05" d="M5.8 14c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7H2.1a11 11 0 0 0 0 9.9L5.8 14z" /><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.2-3.2A11 11 0 0 0 2.1 7l3.7 2.9C6.7 7.3 9.1 5.4 12 5.4z" /></svg>
);

const ReviewCard = ({ r, i }) => (
  <figure className="flex h-full flex-col rounded-2xl border border-line bg-white p-7 shadow-premium" data-testid={`review-card-${i}`}>
    <div className="flex items-center justify-between">
      <Quote className="h-7 w-7 text-brand-red/25" />
      <GoogleMark />
    </div>
    <Stars n={r.rating} className="mt-4 h-4 w-4" />
    <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-slate-700 line-clamp-[7]">"{r.text}"</blockquote>
    <figcaption className="mt-6 flex items-center gap-3 border-t border-line pt-4">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy text-sm font-bold uppercase text-white">{(r.author || "G").trim().charAt(0)}</span>
      <span>
        <span className="block font-semibold text-navy">{r.author}</span>
        <span className="block text-xs text-slate-500">Google review{r.review_date ? ` · ${r.review_date}` : ""}</span>
      </span>
    </figcaption>
  </figure>
);

const usePerView = () => {
  const [pv, setPv] = useState(1);
  useEffect(() => {
    const calc = () => setPv(window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return pv;
};

export const ReviewsSection = ({ service, className = "bg-white" }) => {
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(0);
  const perView = usePerView();
  const hover = useRef(false);

  useEffect(() => {
    api.get("/reviews", { params: { service, limit: 9 } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [service]);

  const pages = items ? Math.max(1, Math.ceil(items.length / perView)) : 1;
  useEffect(() => { setPage((p) => Math.min(p, pages - 1)); }, [pages]);

  useEffect(() => {
    if (!items || items.length <= perView) return;
    const id = setInterval(() => { if (!hover.current) setPage((p) => (p + 1) % pages); }, 5500);
    return () => clearInterval(id);
  }, [items, perView, pages]);

  if (!items?.length) return null;
  const multi = items.length > perView;

  return (
    <section className={`py-24 ${className}`} data-testid="reviews-section">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Eyebrow>Client reviews</Eyebrow>
            <h2 className="mt-4 font-display text-3xl text-navy sm:text-4xl">What our clients say on <em className="text-brand-red">Google.</em></h2>
          </div>
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-outline self-start md:self-auto" data-testid="reviews-google-link"><GoogleMark /> See all reviews on Google <ExternalLink className="h-4 w-4" /></a>
        </div>

        <div className="mt-12 overflow-hidden" onMouseEnter={() => (hover.current = true)} onMouseLeave={() => (hover.current = false)}>
          <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${page * 100}%)` }}>
            {items.map((r, i) => (
              <div key={r.id || i} className="min-w-full px-2.5 sm:min-w-[50%] lg:min-w-[33.3333%]">
                <ReviewCard r={r} i={i} />
              </div>
            ))}
          </div>
        </div>

        {multi && (
          <div className="mt-8 flex items-center justify-center gap-4" data-testid="reviews-controls">
            <button onClick={() => setPage((p) => (p - 1 + pages) % pages)} aria-label="Previous reviews" className="grid h-10 w-10 place-items-center rounded-full border border-line bg-white text-navy transition-colors hover:bg-navy hover:text-white" data-testid="reviews-prev"><ChevronLeft className="h-5 w-5" /></button>
            <div className="flex gap-2">
              {Array.from({ length: pages }).map((_, i) => (
                <button key={i} onClick={() => setPage(i)} aria-label={`Go to slide ${i + 1}`} className={`h-2 rounded-full transition-all ${i === page ? "w-7 bg-brand-red" : "w-2 bg-navy/20 hover:bg-navy/40"}`} data-testid={`reviews-dot-${i}`} />
              ))}
            </div>
            <button onClick={() => setPage((p) => (p + 1) % pages)} aria-label="Next reviews" className="grid h-10 w-10 place-items-center rounded-full border border-line bg-white text-navy transition-colors hover:bg-navy hover:text-white" data-testid="reviews-next"><ChevronRight className="h-5 w-5" /></button>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-500">Reviews are shared by clients on our Google Business Profile. Past results do not guarantee future outcomes.</p>
      </div>
    </section>
  );
};
